// src/utils/eventTypeStyles.js
//
// Single source of truth for the per-event-type color palette across
// every surface that renders a calendar event:
//
//   - CalendarGrid       (compact pills on the month/week grid)
//   - EventCard          (soft cards in popups / lists)
//   - EventListView      (Today's / Selected-date list rows)
//   - DriverQueue        (admin queue page rows)
//
// **⚠️ Tailwind JIT caveat:** Tailwind's compiler scans source for literal
// class strings. Dynamic strings like ``bg-${color}-50`` are NOT picked up
// and get purged from the bundle. So every class in this file is written
// as a complete literal — `bg-blue-50`, not `bg-${color}-50`. When adding
// a new color, write ALL ten variants out by hand.
//
// **Add new event types HERE first, then everywhere else.** Anything not
// in this table falls through to a slate-gray default — visible but
// distinguishable from named types so missing-entry bugs show up
// immediately on screen.

const STYLES = {
  measurement: {
    color: "blue",
    name: "Measurement",
    icon: "📏",
    bg: "bg-blue-50",
    bgIcon: "bg-blue-100",
    text: "text-blue-700",
    textStrong: "text-blue-800",
    border: "border-blue-200",
    borderLeft: "border-l-4 border-l-blue-500",
    ring: "ring-blue-200",
    bgSolid: "bg-blue-500",
    bgSolidHover: "hover:bg-blue-600",
    dot: "bg-blue-500",
  },
  installation: {
    color: "green",
    name: "Installation",
    icon: "🔧",
    bg: "bg-green-50",
    bgIcon: "bg-green-100",
    text: "text-green-700",
    textStrong: "text-green-800",
    border: "border-green-200",
    borderLeft: "border-l-4 border-l-green-500",
    ring: "ring-green-200",
    bgSolid: "bg-green-500",
    bgSolidHover: "hover:bg-green-600",
    dot: "bg-green-500",
  },
  price: {
    color: "purple",
    name: "Pricing",
    icon: "💰",
    bg: "bg-purple-50",
    bgIcon: "bg-purple-100",
    text: "text-purple-700",
    textStrong: "text-purple-800",
    border: "border-purple-200",
    borderLeft: "border-l-4 border-l-purple-500",
    ring: "ring-purple-200",
    bgSolid: "bg-purple-500",
    bgSolidHover: "hover:bg-purple-600",
    dot: "bg-purple-500",
  },
  "follow-up": {
    color: "teal",
    name: "Follow-up",
    icon: "📞",
    bg: "bg-teal-50",
    bgIcon: "bg-teal-100",
    text: "text-teal-700",
    textStrong: "text-teal-800",
    border: "border-teal-200",
    borderLeft: "border-l-4 border-l-teal-500",
    ring: "ring-teal-200",
    bgSolid: "bg-teal-500",
    bgSolidHover: "hover:bg-teal-600",
    dot: "bg-teal-500",
  },
  quote: {
    color: "slate",
    name: "Quote",
    icon: "📋",
    bg: "bg-slate-50",
    bgIcon: "bg-slate-100",
    text: "text-slate-700",
    textStrong: "text-slate-800",
    border: "border-slate-200",
    borderLeft: "border-l-4 border-l-slate-500",
    ring: "ring-slate-200",
    bgSolid: "bg-slate-500",
    bgSolidHover: "hover:bg-slate-600",
    dot: "bg-slate-500",
  },
  reschedule: {
    color: "orange",
    name: "Reschedule",
    icon: "🔄",
    bg: "bg-orange-50",
    bgIcon: "bg-orange-100",
    text: "text-orange-700",
    textStrong: "text-orange-800",
    border: "border-orange-200",
    borderLeft: "border-l-4 border-l-orange-500",
    ring: "ring-orange-200",
    bgSolid: "bg-orange-500",
    bgSolidHover: "hover:bg-orange-600",
    dot: "bg-orange-500",
  },
  pickup: {
    color: "cyan",
    name: "Pick Up",
    icon: "📦",
    bg: "bg-cyan-50",
    bgIcon: "bg-cyan-100",
    text: "text-cyan-700",
    textStrong: "text-cyan-800",
    border: "border-cyan-200",
    borderLeft: "border-l-4 border-l-cyan-500",
    ring: "ring-cyan-200",
    bgSolid: "bg-cyan-500",
    bgSolidHover: "hover:bg-cyan-600",
    dot: "bg-cyan-500",
  },
  delivery: {
    color: "red",
    name: "Delivery",
    icon: "🚚",
    bg: "bg-red-50",
    bgIcon: "bg-red-100",
    text: "text-red-700",
    textStrong: "text-red-800",
    border: "border-red-200",
    borderLeft: "border-l-4 border-l-red-500",
    ring: "ring-red-200",
    bgSolid: "bg-red-500",
    bgSolidHover: "hover:bg-red-600",
    dot: "bg-red-500",
  },
  payment: {
    color: "emerald",
    name: "Payment",
    icon: "💳",
    bg: "bg-emerald-50",
    bgIcon: "bg-emerald-100",
    text: "text-emerald-700",
    textStrong: "text-emerald-800",
    border: "border-emerald-200",
    borderLeft: "border-l-4 border-l-emerald-500",
    ring: "ring-emerald-200",
    bgSolid: "bg-emerald-500",
    bgSolidHover: "hover:bg-emerald-600",
    dot: "bg-emerald-500",
  },
  repair: {
    color: "amber",
    name: "Repair",
    icon: "🛠",
    bg: "bg-amber-50",
    bgIcon: "bg-amber-100",
    text: "text-amber-700",
    textStrong: "text-amber-800",
    border: "border-amber-200",
    borderLeft: "border-l-4 border-l-amber-500",
    ring: "ring-amber-200",
    bgSolid: "bg-amber-500",
    bgSolidHover: "hover:bg-amber-600",
    dot: "bg-amber-500",
  },
};

const FALLBACK = {
  color: "gray",
  name: "Event",
  icon: "📅",
  bg: "bg-gray-50",
  bgIcon: "bg-gray-100",
  text: "text-gray-700",
  textStrong: "text-gray-800",
  border: "border-gray-200",
  borderLeft: "border-l-4 border-l-gray-500",
  ring: "ring-gray-200",
  bgSolid: "bg-gray-500",
  bgSolidHover: "hover:bg-gray-600",
  dot: "bg-gray-500",
};

/**
 * Return the style object for an event type.
 *
 * @param {string} type  e.g. "measurement", "follow-up", "payment".
 *                       Unknown types fall through to a visible gray
 *                       default so missing entries surface in the UI.
 * @returns {Object}     Style bag — see file header for the full list of
 *                       returned keys.
 */
export function getEventTypeStyle(type) {
  return STYLES[type] || FALLBACK;
}

/**
 * Canonical ordered list of event types. Used for filter UIs and
 * picker generation. Order matches the Create Event modals.
 */
export const ALL_EVENT_TYPES = Object.keys(STYLES);

export default {
  getEventTypeStyle,
  ALL_EVENT_TYPES,
};
